import { useState } from 'react';
import { Dialog } from '@cw-ui/feedback-components';
import { formatMessage } from 'src/i18n/intl';
import Locale from 'src/i18n';
import { FileUpload } from '@cw-ui/form-components';
import { useSelector, useDispatch } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import { Button } from '@mui/material';
import { generateBoundary } from 'src/services/ApiService';
import { useHandleFilesUploadMutation } from '../../services/bulkUserImportService';
import {
    setUploadDialogOpen,
    bulkImportUsersSliceId,
    setShouldFileUpload,
    setIsUploadFileFailed,
    setCurrentFileId,
    setCurrentFileName,
    setIsUploading,
} from '../../slice';
import { buildMultipartBody } from '../../utils/util';
import * as Styled from './UploadDialog.styled';

const UploadDialog = () => {
    const dispatch = useDispatch();
    const [handleFilesUpload] = useHandleFilesUploadMutation();
    const [uploadedFile, setUploadedFile] = useState(null);
    const isUploadDialogOpen = useSelector(state => state[bulkImportUsersSliceId]?.isUploadDialogOpen);
    const isFileUploadDisabled = useSelector(state => state[bulkImportUsersSliceId]?.isFileUploadDisabled);

    const handleClose = () => {
        dispatch(setUploadDialogOpen());
    };

    const handleFilesChange = files => {
        dispatch(setShouldFileUpload(files.length > 0));
        setUploadedFile(files[0]);
    };

    const handleUpload = async () => {
        if (uploadedFile) {
            try {
                handleClose();
                dispatch(setIsUploading(true));
                const boundary = generateBoundary();
                const payload = await buildMultipartBody(uploadedFile, boundary);
                const contentType = `multipart/form-data; boundary=${boundary}; version=3.0`;

                const response = await handleFilesUpload({ payload, contentType }).unwrap();
                const uploadId = response?.uploadMetaData?.id;
                const fileName = response?.uploadMetaData?.source || uploadedFile.name;

                if (uploadId) {
                    dispatch(setCurrentFileId(uploadId));
                    dispatch(setCurrentFileName(fileName)); // Add this line
                }

                setUploadedFile('');
            } catch (error) {
                dispatch(setIsUploadFileFailed());
                console.error('File upload failed:', error);
            } finally {
                dispatch(setIsUploading(false));
            }
        }
    };

    return (
        <Dialog
            open={isUploadDialogOpen}
            onClose={handleClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description">
            <Styled.DialogTitle id="alert-dialog-title">
                {formatMessage(Locale.bulkUserImportModalHeader)}
            </Styled.DialogTitle>
            <Styled.Icon aria-label="close" onClick={handleClose}>
                <CloseIcon />
            </Styled.Icon>
            <Styled.DialogContent>
                <Styled.DialogContentL1 id="alert-dialog-description">
                    {formatMessage(Locale.bulkUserImportModalBody1)}
                </Styled.DialogContentL1>
                <Styled.DialogContentL2 id="alert-dialog-description">
                    {formatMessage(Locale.bulkUserImportModalBody2)}
                </Styled.DialogContentL2>
                <Styled.FileUploadContainer>
                    <FileUpload
                        acceptTypes={['.csv']}
                        fileSelect="choose the file"
                        fileSizeErrorText=""
                        fileTypeErrorText=""
                        locale="en_US"
                        messageText="Drag and drop your file here or "
                        onFilesChange={handleFilesChange}
                        stacked
                        disabled={isFileUploadDisabled}
                        sx={{ dropZoneSx: { padding: 1 } }}
                    />
                </Styled.FileUploadContainer>
            </Styled.DialogContent>
            <Styled.Actions>
                <Button size="medium" variant="outlined" color="secondary" onClick={handleClose}>
                    Cancel
                </Button>
                <Button
                    size="medium"
                    variant="contained"
                    color="primary"
                    disabled={!isFileUploadDisabled}
                    onClick={handleUpload}>
                    Upload
                </Button>
            </Styled.Actions>
        </Dialog>
    );
};

export default UploadDialog;
